import React, { useState } from 'react';

import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { Switch } from '@ui-library/Switch';
import { Button } from '@ui-library/Button/Button';
import { Tooltip } from '../Tooltip/Tooltip';

import type { IAgentSettingsMenuProps } from './AgentSettingsMenu.types';
import { AGENT_SETTINGS_CONFIG } from './AgentSettingsMenu.types';

export const AgentSettingsMenu: React.FC<IAgentSettingsMenuProps> = ({
  settings,
  onChange,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);

  const activeCount = Object.values(settings).filter(Boolean).length;

  const dropdownContent = (onClose: () => void) => (
    <div
      className="bg-bg-primary overflow-hidden min-w-[240px]"
      role="menu"
      aria-label="Agent Settings"
    >
      <div className="flex items-center justify-between px-bg-v2-md py-bg-v2-md">
        <Text variant="text-xs" weight="semibold" className="text-text-quaternary tracking-widest uppercase">
          Agent Settings
        </Text>
      </div>

      <div className="flex flex-col gap-bg-v2-xxs">
        {AGENT_SETTINGS_CONFIG.map((cfg) => {
          const isOn = !!settings[cfg.key];
          return (
            <div
              key={cfg.key}
              className="flex items-center gap-bg-v2-md rounded-bg-v2-lg px-bg-v2-md py-bg-v2-md hover:bg-bg-secondary transition-colors duration-150"
            >
              <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                <Text variant="text-sm" weight="medium" className="text-text-primary">
                  {cfg.label}
                </Text>
                <Text variant="text-xs" className="text-text-tertiary leading-snug">
                  {cfg.description}
                </Text>
              </span>
              <Switch
                size="sm"
                checked={isOn}
                disabled={disabled}
                onChange={() => {
                  onChange({ ...settings, [cfg.key]: !isOn });
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <Tooltip
      content={dropdownContent}
      position="top"
      interactive
      trigger="contextmenu"
      delay={0}
      onVisibleChange={setOpen}
    >
      <Button
        type="button"
        disabled={disabled}
        variant="outline"
        size="small"
        className={`
          !rounded-bg-v2-full !gap-bg-v2-sm transition-all duration-150 border-border-secondary
          ${open
            ? '!bg-bg-primary !border-border-primary'
            : '!bg-bg-secondary hover:!bg-bg-primary-hover hover:!border-border-primary'
          }
        `}
        label={
          <>
            <Icon
              name={IconName.Settings}
              size="sm"
              className={`transition-colors duration-150 ${open || activeCount > 0 ? 'text-text-primary' : 'text-text-secondary'}`}
              inheritColor
            />
            {activeCount > 0 && (
              <span className="flex items-center justify-center w-4 h-4 rounded-full bg-bg-quaternary text-text-primary text-[10px] font-semibold leading-none">
                {activeCount}
              </span>
            )}
            <Icon
              name={IconName.ChevronDown}
              size="sm"
              className={`transition-all duration-200 ${open ? 'text-text-primary rotate-180' : 'text-text-quaternary'}`}
              inheritColor
            />
          </>
        }
      />
    </Tooltip>
  );
};
